<template>
  <div v-if="authenticated">
    <b-form @submit="onSubmit">
      <KTPortlet :title="title">
        <template v-slot:body>
          <b-row align-h="end">
            <b-col :cols="4">
              <b-input v-model="search" placeholder="Quick Search...." />
            </b-col>
          </b-row>
          <v-data-table
            v-model="enabledFeatures"
            :headers="headers"
            :items="featuresList"
            item-key="Alias"
            show-select
            class="elevation-1"
            :search="search"
            :loading="loading"
          >
            <template v-slot:loading>
              <b-spinner variant="primary" label="Spinning"></b-spinner>
            </template>
          </v-data-table>
        </template>
        <template v-slot:foot>
          <b-row class="d-flex justify-content-between">
            <b-button type="submit" variant="primary">Save</b-button>
            <b-button type="button" @click="cancel" variant="secondary"
              >Cancel</b-button
            >
          </b-row>
        </template>
      </KTPortlet>
    </b-form>
    <LoadingModal v-bind="loadingModal" />
    <AlertModal
      v-bind="alertModal"
      v-model="alertModal.show"
      @dismiss="resetAlert"
    />
    <ConfirmationModal
      v-bind="confirmationModal"
      v-model="confirmationModal.show"
      @dismiss="resetConfirmation"
    />
  </div>
</template>

<script>
import KTPortlet from "@/views/partials/content/Portlet";
import { API, graphqlOperation } from "aws-amplify";
import AlertModalMixin from "@/components/widgets/AlertModalMixin.js";

const AlertModal = AlertModalMixin.AlertModal;
const LoadingModal = AlertModalMixin.LoadingModal;
const ConfirmationModal = AlertModalMixin.ConfirmationModal;

export default {
  mixins: [AlertModalMixin],
  components: {
    KTPortlet
  },
  props: {
    tenantAlias: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      enabledFeatures: [],
      currentMerchantFeatures: [],
      featuresList: [],
      stopLoading: false,
      search: ""
    };
  },
  computed: {
    title() {
      return `Set Features For ${this.tenantAlias}`;
    },
    loading() {
      return (
        !this.stopLoading && (!this.featuresList || !this.featuresList.length)
      );
    },
    authenticated() {
      return this.$role.verify([this.$role.ADMIN]);
    },
    headers() {
      return [
        {
          text: "Name",
          value: "Name"
        },
        {
          text: "Description",
          value: "Description"
        }
      ];
    }
  },
  methods: {
    async getFeatures() {
      const getFeatures = `
              query GetAllFeatures {
                  getAllFeatures {
                      features{
                          Alias
                          Name
                          Description
                      }
                  }
              }
            `;

      const getFeaturesResponse = await API.graphql({
        ...graphqlOperation(getFeatures)
      });

      return getFeaturesResponse.data.getAllFeatures.features;
    },
    async getEnabledFeatures(tenantAlias) {
      const getMerchantFeatures = `
              query GetMerchantFeatures(
                $aliasCode: String!
              ) {
                getMerchantFeatures(aliasCode: $aliasCode)
              }
            `;

      const getMerchantFeaturesResponse = await API.graphql({
        ...graphqlOperation(getMerchantFeatures, { aliasCode: tenantAlias })
      });

      return getMerchantFeaturesResponse.data.getMerchantFeatures;
    },
    setupFeaturesTable: async function() {
      const [allFeatures, enabledFeatures] = await Promise.all([
        this.getFeatures(),
        this.getEnabledFeatures(this.tenantAlias)
      ]);
      this.currentMerchantFeatures = enabledFeatures ?? [];
      this.addToFeaturesList(allFeatures);
    },
    addToFeaturesList(additionalFeatures) {
      this.featuresList = this.featuresList.concat(additionalFeatures);
      this.enabledFeatures = this.enabledFeatures.concat(
        additionalFeatures.reduce(this.findEnabledFeatures, [])
      );
    },
    findEnabledFeatures(selected, feature) {
      if (this.currentMerchantFeatures.includes(feature.Alias))
        selected.push(feature);
      return selected;
    },
    async updateMerchantFeatures(tenantAlais, enabledFeatures) {
      const setMerchantFeatures = `
          mutation SetMerchantFeatures($params: SetMerchantFeaturesInput!) {
            setMerchantFeatures(params: $params)
          }
        `;

      try {
        const enabledFeaturesArray = enabledFeatures.map(
          feature => feature.Alias
        );

        const params = {
          aliasCode: tenantAlais,
          features: enabledFeaturesArray
        };

        const setMerchantFeaturesResponse = await API.graphql({
          ...graphqlOperation(setMerchantFeatures, { params })
        });

        return setMerchantFeaturesResponse.data.setMerchantFeatures;
      } catch (e) {
        this.$logger.error(
          `Error updating merchant features for ${tenantAlais}`
        );
        this.$logger.error(e);
      }
      return false;
    },
    async onSubmit(e) {
      e.preventDefault();
      this.startLoadingDialog(`Saving Features`);
      const result = await this.updateMerchantFeatures(
        this.tenantAlias,
        this.enabledFeatures
      );
      if (result) {
        await this.stopLoadingDialog(
          `Features for ${this.tenantAlias} have been saved`,
          AlertModal.SUCCESS
        );
        this.goBack();
      } else {
        this.stopLoadingDialog(
          `There was an error modifying features for ${this.tenantAlias}.`,
          AlertModal.ERROR
        );
      }
    },
    cancel() {
      this.goBack();
    },
    goBack() {
      this.$emit("back");
    }
  },
  created: async function() {
    try {
      await this.setupFeaturesTable();
    } catch (e) {
      this.stopLoading = true;
      this.$logger.error(error);
      this.alert(
        `Unable to retrieve features for ${this.tenantAlias}.`,
        AlertModal.ERROR
      );
    }
  }
};
</script>

<style lang="scss" scoped></style>
